import {toWikitext, wikiSections, locateWikiAnchor} from './wikitext.js';
import {defaultBody} from './articleStructure.js';
import {setClassification} from './categories.js';
import {recordEditorChange, restoreEditorChange} from './editorHistory.js';
import {editorChanges} from './editorChanges.js';

export const draftFingerprint = draft => JSON.stringify(draft);
export function canApplySuggestion(draft, suggestion) {
  return Boolean(draft && suggestion && draft.id === suggestion.termId &&
    draft.version === suggestion.version && draftFingerprint(draft) === suggestion.snapshot);
}

// Owns recovery, baseline, operation history and confirmed writes. DOM selection
// stays with the editor; callers never manipulate the history or storage keys.
export function createArticleDraft({storage, userId, onStorageError = () => {}}) {
  let current = null, busy = false, ownerId = null;
  let history = {past: [], future: []};
  const listeners = new Set();
  const key = id => 'wiki-article-draft:' + (ownerId || userId()) + ':' + id;
  const emit = () => listeners.forEach(listener => listener());
  function persist(draft) {
    storage.setItem(key(draft.id), JSON.stringify(draft));
  }
  function replace(next) {
    current = typeof next === 'function' ? next(current) : next;
    if (current) {
      try { persist(current); }
      catch { onStorageError('文章草稿無法儲存到瀏覽器，請先複製內容。'); }
    } else history = {past: [], future: []};
    emit();
  }
  return {
    getSnapshot: () => current,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    replace(next) { if (busy) return false; replace(next); return true; },
    begin(term, {categories = [], section = null, discussion = null} = {}) {
      ownerId = userId();
      let restored;
      try {
        const stored = JSON.parse(storage.getItem(key(term.id)));
        if (stored?.id === term.id && Number.isInteger(stored.version) && stored.version > 0) restored = stored;
      } catch {}
      const draft = restored ? {...restored} : {...term, body: term.body ?? defaultBody(term)};
      if (!draft.classification) {
        const match = categories.find(c => c.name === draft.category || c.previousNames?.includes(draft.category));
        if (match) {
          draft.classification = setClassification(draft, match.id, [], categories);
          if (draft.baseSnapshot && !draft.baseSnapshot.classification) draft.baseSnapshot = {...draft.baseSnapshot, classification: draft.classification};
        }
      }
      if (discussion?.anchor && ['wikitext', 'article', 'definition', 'body'].includes(discussion.anchor.field)) {
        const source = toWikitext(draft), position = locateWikiAnchor(source, discussion.anchor);
        if (position) {
          const index = wikiSections(source).findLastIndex(s => s.start <= position.start);
          if (index >= 0) section = index;
        }
      }
      history = {past: [], future: []};
      replace({...draft, baseSnapshot: draft.baseSnapshot || (draft.version === term.version ? term : null),
        editSummary: restored ? draft.editSummary || '' : '',
        discussionIds: [...new Set([...(restored ? draft.discussionIds || [] : []), ...(discussion ? [discussion.id] : [])])],
        contextDiscussionId: discussion?.id || draft.contextDiscussionId || null,
        wikitext: toWikitext(draft), contentFormat: 'wikitext-v1',
        editInfobox: section === 'infobox', editConcept: section === 'concept'});
      return {restored: Boolean(restored), section: Number.isInteger(section) ? section : null};
    },
    changes(term) { return current ? editorChanges(current.baseSnapshot || term, current) : {dirty: false}; },
    change(patch) {
      if (!current || busy) return false;
      const next = recordEditorChange(history, current, patch);
      if (!next) return false;
      replace(next); return true;
    },
    undo(redo = false) {
      if (!current || busy) return false;
      const next = restoreEditorChange(history, current, redo);
      if (!next) return false;
      replace(next); return true;
    },
    get canUndo() { return Boolean(history.past.length); },
    get canRedo() { return Boolean(history.future.length); },
    applySuggestion(suggestion) {
      if (busy || !canApplySuggestion(current, suggestion)) return false;
      return this.change({wikitext: suggestion.wikitext, ...(suggestion.infobox == null ? {} : {infobox: suggestion.infobox})});
    },
    merge(values, latest) {
      if (!current || busy) return;
      history = {past: [], future: []};
      replace({...current, ...values, baseSnapshot: latest, version: latest.version});
    },
    leave(discard = false) {
      if (!current || busy) return false;
      // A storage failure blocks leaving; do not silently lose the only draft.
      if (discard) storage.removeItem(key(current.id)); else persist(current);
      replace(null); return true;
    },
    async save(api, refresh) {
      if (!current || busy) return null;
      busy = true;
      const draft = current;
      try {
        if (!draft.label?.trim()) throw Error('中文名稱不可留空。');
        if (draft.qid && !/^Q[1-9]\d*$/.test(draft.qid)) throw Error('QID 格式應為 Q 加上數字。');
        if (draft.wikiUrl) {
          let url; try { url = new URL(draft.wikiUrl); } catch {}
          if (!url || !['https:', 'http:'].includes(url.protocol)) throw Error('請填入有效的 http 或 https 網址。');
        }
        const {baseSnapshot, ...payload} = draft;
        const saved = await api('terms/' + draft.id, 'PATCH', payload);
        if (saved.contentFormat !== 'wikitext-v1' || saved.wikitext !== draft.wikitext) throw Error('伺服器未確認原始碼已完整儲存，草稿已保留。');
        let refreshedState = null, warning = '';
        if (refresh) {
          try { refreshedState = await refresh(); } catch {}
          if (!refreshedState) warning = '修改已儲存，但工作台未重新載入；請確認連線。';
        }
        try { storage.removeItem(key(draft.id)); }
        catch { warning += (warning ? ' ' : '') + '修改已儲存，但瀏覽器的舊草稿未清除。'; }
        replace(null);
        return {saved, refreshedState, previous: draft.version, discussionIds: payload.discussionIds || [], warning};
      } finally { busy = false; }
    }
  };
}
